import { html, nothing } from "lit";
import {
  expandToolGroups,
  normalizeToolName,
  resolveToolProfilePolicy,
} from "../../../../src/agents/tool-policy.js";
import { normalizeAgentId } from "../../../../src/routing/session-key.js";
import {
  AGENT_HINTS,
  CONFIG_HINTS,
  hintAgentFile,
  hintForAgentPanel,
  hintSelectAgent,
} from "../control-hints.ts";
import { resolveConfiguredModels } from "../controllers/agents-list-config-patch.ts";
import { formatAgo } from "../format.ts";
import {
  formatCronPayload,
  formatCronSchedule,
  formatCronState,
  formatNextRun,
} from "../presenter.ts";
import type {
  AgentFileEntry,
  AgentsFilesListResult,
  AgentsListResult,
  AgentIdentityResult,
  AgentQueueStatus,
  ChannelAccountSnapshot,
  ChannelsStatusSnapshot,
  CronJob,
  CronStatus,
  SkillStatusEntry,
  SkillStatusReport,
} from "../types.ts";
import { renderAgentsImportPanel, type AgentsImportPanelProps } from "./agents-import-panel.ts";
import { renderAgentOverviewEditor } from "./agents-overview-editor.ts";
import { renderAgentsSettingsPanel, type AgentsSettingsProps } from "./agents-settings-panel.ts";
import { renderAgentsWorkPlan, type AgentsWorkPlanProps } from "./agents-work-plan-panel.ts";
import { renderFleetRoleEditor, type FleetRoleEditorProps } from "./fleet-role-editor.ts";
export type AgentsPageTab = "roles" | "list" | "import" | "settings";

export const AGENTS_PAGE_TABS: Array<{ id: AgentsPageTab; label: string }> = [
  { id: "roles", label: "Roles" },
  { id: "list", label: "Instances" },
  { id: "import", label: "Load from chat" },
  { id: "settings", label: "Settings" },
];

export type AgentsPanel =
  | "overview"
  | "files"
  | "tools"
  | "skills"
  | "channels"
  | "cron"
  | "workplan"
  | "sandbox";

export type AgentsProps = {
  loading: boolean;
  error: string | null;
  agentsList: AgentsListResult | null;
  selectedAgentId: string | null;
  activePanel: AgentsPanel;
  configForm: Record<string, unknown> | null;
  configIssues: unknown[];
  configLoading: boolean;
  configSaving: boolean;
  configDirty: boolean;
  channelsLoading: boolean;
  channelsError: string | null;
  channelsSnapshot: ChannelsStatusSnapshot | null;
  channelsLastSuccess: number | null;
  cronLoading: boolean;
  cronStatus: CronStatus | null;
  cronJobs: CronJob[];
  cronError: string | null;
  agentFilesLoading: boolean;
  agentFilesError: string | null;
  agentFilesList: AgentsFilesListResult | null;
  agentFileActive: string | null;
  agentFileContents: Record<string, string>;
  agentFileDrafts: Record<string, string>;
  agentFileSaving: boolean;
  agentIdentityLoading: boolean;
  agentIdentityError: string | null;
  agentIdentityById: Record<string, AgentIdentityResult>;
  agentSkillsLoading: boolean;
  agentSkillsReport: SkillStatusReport | null;
  agentSkillsError: string | null;
  agentSkillsAgentId: string | null;
  skillsFilter: string;
  onRefresh: () => void;
  onSelectAgent: (agentId: string) => void;
  onSelectPanel: (panel: AgentsPanel) => void;
  onLoadFiles: (agentId: string) => void;
  onSelectFile: (name: string) => void;
  onFileDraftChange: (name: string, content: string) => void;
  onFileReset: (name: string) => void;
  onFileSave: (name: string) => void;
  onToolsProfileChange: (agentId: string, profile: string | null, clearAllow: boolean) => void;
  onToolsOverridesChange: (agentId: string, alsoAllow: string[], deny: string[]) => void;
  onConfigReload: () => void;
  onConfigSave: () => void;
  onChannelsRefresh: () => void;
  onCronRefresh: () => void;
  onSkillsFilterChange: (next: string) => void;
  onSkillsRefresh: () => void;
  onAgentSkillToggle: (agentId: string, skillName: string, enabled: boolean) => void;
  onAgentSkillsClear: (agentId: string) => void;
  onAgentSkillsDisableAll: (agentId: string) => void;
  onSetInstanceDefault: (agentId: string) => void;
  onOpenAddAgent: () => void;
  onRegisterAgent: (agentId: string) => void;
  onDeleteAgent: (agentId: string) => void;
  onAgentFieldChange: (
    agentId: string,
    field: import("../controllers/agents-config.ts").AgentFieldPatch,
  ) => void;
  onSandboxChange: (
    agentId: string,
    patch: import("../controllers/agents-config.ts").SandboxFieldPatch,
  ) => void;
  workPlan: AgentsWorkPlanProps | null;
  pageTab: AgentsPageTab;
  onPageTabChange: (tab: AgentsPageTab) => void;
  roles: FleetRoleEditorProps;
  importFromChat: AgentsImportPanelProps;
  queueStatus: AgentQueueStatus | null;
  queueLoading: boolean;
  queueError: string | null;
  concurrencyValue: number;
  concurrencyDirty: boolean;
  concurrencySaving: boolean;
  onConcurrencyChange: (value: number) => void;
  onConcurrencySave: () => void;
  onQueueRefresh: () => void;
};

const TOOL_SECTIONS = [
  {
    id: "fs",
    label: "Files",
    tools: [
      { id: "read", label: "read", description: "Read file contents" },
      { id: "write", label: "write", description: "Create or overwrite files" },
      { id: "edit", label: "edit", description: "Make precise edits" },
      { id: "apply_patch", label: "apply_patch", description: "Patch files (OpenAI)" },
    ],
  },
  {
    id: "runtime",
    label: "Runtime",
    tools: [
      { id: "exec", label: "exec", description: "Run shell commands" },
      { id: "process", label: "process", description: "Manage background processes" },
    ],
  },
  {
    id: "web",
    label: "Web",
    tools: [
      {
        id: "web_search",
        label: "web_search",
        description:
          "Search the web (Brave or Perplexity; configure at Config → Tools → Web Search)",
      },
      { id: "web_fetch", label: "web_fetch", description: "Fetch web content" },
    ],
  },
  {
    id: "memory",
    label: "Memory",
    tools: [
      { id: "memory_search", label: "memory_search", description: "Semantic search" },
      { id: "memory_get", label: "memory_get", description: "Read memory files" },
    ],
  },
  {
    id: "sessions",
    label: "Sessions",
    tools: [
      { id: "sessions_list", label: "sessions_list", description: "List sessions" },
      { id: "sessions_history", label: "sessions_history", description: "Session history" },
      { id: "sessions_send", label: "sessions_send", description: "Send to session" },
      { id: "sessions_spawn", label: "sessions_spawn", description: "Spawn sub-agent" },
      { id: "session_status", label: "session_status", description: "Session status" },
    ],
  },
  {
    id: "ui",
    label: "UI",
    tools: [
      { id: "browser", label: "browser", description: "Control web browser" },
      { id: "canvas", label: "canvas", description: "Control canvases" },
    ],
  },
  {
    id: "messaging",
    label: "Messaging",
    tools: [{ id: "message", label: "message", description: "Send messages" }],
  },
  {
    id: "automation",
    label: "Automation",
    tools: [
      { id: "cron", label: "cron", description: "Schedule tasks" },
      { id: "gateway", label: "gateway", description: "Gateway control" },
    ],
  },
  {
    id: "nodes",
    label: "Nodes",
    tools: [{ id: "nodes", label: "nodes", description: "Nodes + devices" }],
  },
  {
    id: "agents",
    label: "Agents",
    tools: [{ id: "agents_list", label: "agents_list", description: "List agents" }],
  },
  {
    id: "media",
    label: "Media",
    tools: [{ id: "image", label: "image", description: "Image understanding" }],
  },
];

const PROFILE_OPTIONS = [
  { id: "minimal", label: "Minimal" },
  { id: "coding", label: "Coding" },
  { id: "messaging", label: "Messaging" },
  { id: "full", label: "Full" },
] as const;

type ToolPolicy = {
  allow?: string[];
  deny?: string[];
};

type AgentConfigEntry = {
  id: string;
  name?: string;
  workspace?: string;
  agentDir?: string;
  model?: unknown;
  skills?: string[];
  /**
   * The role this agent is bound to.
   *
   * JAISIU-4515 §4 — the model is assigned to the ROLE, so the agent
   * panel points the operator at the role rather than offering a
   * per-agent choice.
   */
  roleId?: string;
  tools?: {
    profile?: string;
    allow?: string[];
    alsoAllow?: string[];
    deny?: string[];
  };
};

type ConfigSnapshot = {
  agents?: {
    defaults?: { workspace?: string; model?: unknown; models?: Record<string, { alias?: string }> };
    list?: AgentConfigEntry[];
  };
  tools?: {
    profile?: string;
    allow?: string[];
    alsoAllow?: string[];
    deny?: string[];
  };
};

function normalizeAgentLabel(agent: { id: string; name?: string; identity?: { name?: string } }) {
  return agent.name?.trim() || agent.identity?.name?.trim() || agent.id;
}

function isLikelyEmoji(value: string) {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  if (trimmed.length > 16) {
    return false;
  }
  let hasNonAscii = false;
  for (let i = 0; i < trimmed.length; i += 1) {
    if (trimmed.charCodeAt(i) > 127) {
      hasNonAscii = true;
      break;
    }
  }
  if (!hasNonAscii) {
    return false;
  }
  if (trimmed.includes("://") || trimmed.includes("/") || trimmed.includes(".")) {
    return false;
  }
  return true;
}

function resolveAgentEmoji(
  agent: { identity?: { emoji?: string; avatar?: string } },
  agentIdentity?: AgentIdentityResult | null,
) {
  const identityEmoji = agentIdentity?.emoji?.trim();
  if (identityEmoji && isLikelyEmoji(identityEmoji)) {
    return identityEmoji;
  }
  const agentEmoji = agent.identity?.emoji?.trim();
  if (agentEmoji && isLikelyEmoji(agentEmoji)) {
    return agentEmoji;
  }
  const identityAvatar = agentIdentity?.avatar?.trim();
  if (identityAvatar && isLikelyEmoji(identityAvatar)) {
    return identityAvatar;
  }
  const avatar = agent.identity?.avatar?.trim();
  if (avatar && isLikelyEmoji(avatar)) {
    return avatar;
  }
  return "";
}

function agentBadgeText(agentId: string, defaultId: string | null) {
  return defaultId && agentId === defaultId ? "default" : null;
}

function formatBytes(bytes?: number) {
  if (bytes == null || !Number.isFinite(bytes)) {
    return "-";
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const units = ["KB", "MB", "GB", "TB"];
  let size = bytes / 1024;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(size < 10 ? 1 : 0)} ${units[unitIndex]}`;
}

function resolveAgentConfig(config: Record<string, unknown> | null, agentId: string) {
  const cfg = config as ConfigSnapshot | null;
  const list = cfg?.agents?.list ?? [];
  const normalized = normalizeAgentId(agentId);
  const entry = list.find(
    (agent) =>
      agent &&
      typeof agent === "object" &&
      typeof (agent as { id?: unknown }).id === "string" &&
      normalizeAgentId((agent as { id: string }).id) === normalized,
  );
  return {
    entry,
    defaults: cfg?.agents?.defaults,
    globalTools: cfg?.tools,
  };
}

type AgentContext = {
  workspace: string;
  model: string;
  identityName: string;
  identityEmoji: string;
  skillsLabel: string;
  isDefault: boolean;
};

function buildAgentContext(
  agent: AgentsListResult["agents"][number],
  configForm: Record<string, unknown> | null,
  agentFilesList: AgentsFilesListResult | null,
  defaultId: string | null,
  agentIdentity?: AgentIdentityResult | null,
): AgentContext {
  const config = resolveAgentConfig(configForm, agent.id);
  const workspaceFromFiles =
    agentFilesList && agentFilesList.agentId === agent.id ? agentFilesList.workspace : null;
  const workspace =
    workspaceFromFiles || config.entry?.workspace || config.defaults?.workspace || "default";
  const modelLabel = config.entry?.model
    ? resolveModelLabel(config.entry?.model)
    : resolveModelLabel(config.defaults?.model);
  const identityName =
    agentIdentity?.name?.trim() ||
    agent.identity?.name?.trim() ||
    agent.name?.trim() ||
    config.entry?.name ||
    agent.id;
  const identityEmoji = resolveAgentEmoji(agent, agentIdentity) || "-";
  const skillFilter = Array.isArray(config.entry?.skills) ? config.entry?.skills : null;
  const skillCount = skillFilter?.length ?? null;
  return {
    workspace,
    model: modelLabel,
    identityName,
    identityEmoji,
    skillsLabel: skillFilter ? `${skillCount} selected` : "all skills",
    isDefault: Boolean(defaultId && agent.id === defaultId),
  };
}

function resolveModelLabel(model?: unknown): string {
  if (!model) {
    return "-";
  }
  if (typeof model === "string") {
    return model.trim() || "-";
  }
  if (typeof model === "object" && model) {
    const record = model as { primary?: string; fallbacks?: string[] };
    const primary = record.primary?.trim();
    if (primary) {
      const fallbackCount = Array.isArray(record.fallbacks) ? record.fallbacks.length : 0;
      return fallbackCount > 0 ? `${primary} (+${fallbackCount} fallback)` : primary;
    }
  }
  return "-";
}

function normalizeModelValue(label: string): string {
  const match = label.match(/^(.+) \(\+\d+ fallback\)$/);
  return match ? match[1] : label;
}

function resolveModelPrimary(model?: unknown): string | null {
  if (!model) {
    return null;
  }
  if (typeof model === "string") {
    const trimmed = model.trim();
    return trimmed || null;
  }
  if (typeof model === "object" && model) {
    const record = model as Record<string, unknown>;
    const candidate =
      typeof record.primary === "string"
        ? record.primary
        : typeof record.model === "string"
          ? record.model
          : typeof record.id === "string"
            ? record.id
            : typeof record.value === "string"
              ? record.value
              : null;
    const primary = candidate?.trim();
    return primary || null;
  }
  return null;
}

function resolveModelFallbacks(model?: unknown): string[] | null {
  if (!model || typeof model === "string") {
    return null;
  }
  if (typeof model === "object" && model) {
    const record = model as Record<string, unknown>;
    const fallbacks = Array.isArray(record.fallbacks)
      ? record.fallbacks
      : Array.isArray(record.fallback)
        ? record.fallback
        : null;
    return fallbacks
      ? fallbacks.filter((entry): entry is string => typeof entry === "string")
      : null;
  }
  return null;
}

function parseFallbackList(value: string): string[] {
  return value
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function buildModelOptions(configForm: Record<string, unknown> | null, current?: string | null) {
  const options = resolveConfiguredModels(configForm);
  const hasCurrent = current ? options.some((option) => option.value === current) : false;
  if (current && !hasCurrent) {
    options.unshift({ value: current, label: `Current (${current})` });
  }
  if (options.length === 0) {
    return html` <option value="" disabled>No configured models</option> `;
  }
  return options.map((option) => html`<option value=${option.value}>${option.label}</option>`);
}

type CompiledPattern =
  | { kind: "all" }
  | { kind: "exact"; value: string }
  | { kind: "regex"; value: RegExp };

function compilePattern(pattern: string): CompiledPattern {
  const normalized = normalizeToolName(pattern);
  if (!normalized) {
    return { kind: "exact", value: "" };
  }
  if (normalized === "*") {
    return { kind: "all" };
  }
  if (!normalized.includes("*")) {
    return { kind: "exact", value: normalized };
  }
  const escaped = normalized.replace(/[.*+?^${}()|[\\]\\]/g, "\\$&");
  return { kind: "regex", value: new RegExp(`^${escaped.replaceAll("\\*", ".*")}$`) };
}

function compilePatterns(patterns?: string[]): CompiledPattern[] {
  if (!Array.isArray(patterns)) {
    return [];
  }
  return expandToolGroups(patterns)
    .map(compilePattern)
    .filter((pattern) => {
      return pattern.kind !== "exact" || pattern.value.length > 0;
    });
}

function matchesAny(name: string, patterns: CompiledPattern[]) {
  for (const pattern of patterns) {
    if (pattern.kind === "all") {
      return true;
    }
    if (pattern.kind === "exact" && name === pattern.value) {
      return true;
    }
    if (pattern.kind === "regex" && pattern.value.test(name)) {
      return true;
    }
  }
  return false;
}

function isAllowedByPolicy(name: string, policy?: ToolPolicy) {
  if (!policy) {
    return true;
  }
  const normalized = normalizeToolName(name);
  const deny = compilePatterns(policy.deny);
  if (matchesAny(normalized, deny)) {
    return false;
  }
  const allow = compilePatterns(policy.allow);
  if (allow.length === 0) {
    return true;
  }
  if (matchesAny(normalized, allow)) {
    return true;
  }
  if (normalized === "apply_patch" && matchesAny("exec", allow)) {
    return true;
  }
  return false;
}

function matchesList(name: string, list?: string[]) {
  if (!Array.isArray(list) || list.length === 0) {
    return false;
  }
  const normalized = normalizeToolName(name);
  const patterns = compilePatterns(list);
  if (matchesAny(normalized, patterns)) {
    return true;
  }
  if (normalized === "apply_patch" && matchesAny("exec", patterns)) {
    return true;
  }
  return false;
}

export function renderAgents(props: AgentsProps) {
  const agents = props.agentsList?.agents ?? [];
  const defaultId = props.agentsList?.defaultId ?? null;
  const selectedId = props.selectedAgentId ?? defaultId ?? agents[0]?.id ?? null;
  const selectedAgent = selectedId
    ? (agents.find((agent) => agent.id === selectedId) ?? null)
    : null;

  return html`
    <div class="agents-page">
      <div class="agents-page-tabs" role="tablist" aria-label="Agents sections">
        ${AGENTS_PAGE_TABS.map(
          (tab) => html`
            <button
              type="button"
              class="agents-page-tab ${props.pageTab === tab.id ? "active" : ""}"
              role="tab"
              aria-selected=${props.pageTab === tab.id}
              data-testid=${`agents-page-tab-${tab.id}`}
              @click=${() => props.onPageTabChange(tab.id)}
            >
              ${tab.label}
            </button>
          `,
        )}
      </div>
      ${
        props.pageTab === "roles"
          ? renderFleetRoleEditor(props.roles)
          : props.pageTab === "import"
            ? renderAgentsImportPanel(props.importFromChat)
            : props.pageTab === "settings"
              ? renderAgentsSettingsPanel({
                  queueStatus: props.queueStatus,
                  queueLoading: props.queueLoading,
                  queueError: props.queueError,
                  concurrencyValue: props.concurrencyValue,
                  concurrencyDirty: props.concurrencyDirty,
                  concurrencySaving: props.concurrencySaving,
                  onConcurrencyChange: props.onConcurrencyChange,
                  onConcurrencySave: props.onConcurrencySave,
                  onQueueRefresh: props.onQueueRefresh,
                })
              : html`
                  <div class="agents-layout">
                    <section class="card agents-sidebar">
                      <div class="row" style="justify-content: space-between;">
                        <div>
                          <div class="card-title">Agents</div>
                          <div class="card-sub">${agents.length} configured.</div>
                        </div>
                        <div class="row" style="gap: 8px;">
                          <button
                            type="button"
                            class="btn btn--sm primary"
                            ?disabled=${!props.configForm || props.configLoading}
                            title=${AGENT_HINTS.add}
                            aria-label=${AGENT_HINTS.add}
                            @click=${props.onOpenAddAgent}
                          >
                            Add
                          </button>
                          <button
                            class="btn btn--sm"
                            ?disabled=${props.loading}
                            title=${AGENT_HINTS.refreshList}
                            aria-label=${AGENT_HINTS.refreshList}
                            @click=${props.onRefresh}
                          >
                            ${props.loading ? "Loading…" : "Refresh"}
                          </button>
                        </div>
                      </div>
                      ${
                        props.error
                          ? html`<div class="callout danger" style="margin-top: 12px;">
                              ${props.error}
                            </div>`
                          : nothing
                      }
                      <div class="agent-list" style="margin-top: 12px;">
                        ${
                          agents.length === 0
                            ? html` <div class="muted">No agents found.</div> `
                            : agents.map((agent) => {
                                const badge = agentBadgeText(agent.id, defaultId);
                                const identity = props.agentIdentityById[agent.id] ?? null;
                                const emoji = resolveAgentEmoji(agent, identity);
                                const rowLabel = normalizeAgentLabel(agent);
                                const purpose = identity?.purpose?.trim() || undefined;
                                return html`
                                  <button
                                    type="button"
                                    class="agent-row ${selectedId === agent.id ? "active" : ""}"
                                    title=${hintSelectAgent(rowLabel, agent.id)}
                                    aria-label=${hintSelectAgent(rowLabel, agent.id)}
                                    @click=${() => props.onSelectAgent(agent.id)}
                                  >
                                    <div class="agent-avatar">
                                      ${emoji || normalizeAgentLabel(agent).slice(0, 1)}
                                    </div>
                                    <div class="agent-info">
                                      <div class="agent-title">${normalizeAgentLabel(agent)}</div>
                                      <div class="agent-sub ${purpose ? "" : "mono"}">
                                        ${purpose || agent.id}
                                      </div>
                                    </div>
                                    ${badge ? html`<span class="agent-pill">${badge}</span>` : nothing}
                                  </button>
                                `;
                              })
                        }
                      </div>
                    </section>
                    <section class="agents-main">
                      ${
                        !selectedAgent
                          ? html`
                              <div class="card">
                                <div class="card-title">Select an agent</div>
                                <div class="card-sub">
                                  Pick an agent to inspect its workspace and tools.
                                </div>
                              </div>
                            `
                          : html`
                              ${renderAgentHeader(
                                selectedAgent,
                                defaultId,
                                props.agentIdentityById[selectedAgent.id] ?? null,
                                props.configForm,
                                props.configLoading,
                                props.configSaving,
                                props.onDeleteAgent,
                              )}
                              ${renderAgentTabs(props.activePanel, (panel) => props.onSelectPanel(panel))}
                              ${
                                props.activePanel === "overview"
                                  ? renderAgentOverview({
                                      agent: selectedAgent,
                                      defaultId,
                                      configForm: props.configForm,
                                      configIssues: props.configIssues,
                                      agentFilesList: props.agentFilesList,
                                      agentIdentity:
                                        props.agentIdentityById[selectedAgent.id] ?? null,
                                      agentIdentityError: props.agentIdentityError,
                                      agentIdentityLoading: props.agentIdentityLoading,
                                      configLoading: props.configLoading,
                                      configSaving: props.configSaving,
                                      configDirty: props.configDirty,
                                      onConfigReload: props.onConfigReload,
                                      onConfigSave: props.onConfigSave,
                                      onSetInstanceDefault: props.onSetInstanceDefault,
                                      onRegisterAgent: props.onRegisterAgent,
                                      onAgentFieldChange: props.onAgentFieldChange,
                                    })
                                  : nothing
                              }
                              ${
                                props.activePanel === "files"
                                  ? renderAgentFiles({
                                      agentId: selectedAgent.id,
                                      agentFilesList: props.agentFilesList,
                                      agentFilesLoading: props.agentFilesLoading,
                                      agentFilesError: props.agentFilesError,
                                      agentFileActive: props.agentFileActive,
                                      agentFileContents: props.agentFileContents,
                                      agentFileDrafts: props.agentFileDrafts,
                                      agentFileSaving: props.agentFileSaving,
                                      onLoadFiles: props.onLoadFiles,
                                      onSelectFile: props.onSelectFile,
                                      onFileDraftChange: props.onFileDraftChange,
                                      onFileReset: props.onFileReset,
                                      onFileSave: props.onFileSave,
                                    })
                                  : nothing
                              }
                              ${
                                props.activePanel === "tools"
                                  ? renderAgentTools({
                                      agentId: selectedAgent.id,
                                      configForm: props.configForm,
                                      configLoading: props.configLoading,
                                      configSaving: props.configSaving,
                                      configDirty: props.configDirty,
                                      onProfileChange: props.onToolsProfileChange,
                                      onOverridesChange: props.onToolsOverridesChange,
                                      onConfigReload: props.onConfigReload,
                                      onConfigSave: props.onConfigSave,
                                    })
                                  : nothing
                              }
                              ${
                                props.activePanel === "skills"
                                  ? renderAgentSkills({
                                      agentId: selectedAgent.id,
                                      report: props.agentSkillsReport,
                                      loading: props.agentSkillsLoading,
                                      error: props.agentSkillsError,
                                      activeAgentId: props.agentSkillsAgentId,
                                      configForm: props.configForm,
                                      configLoading: props.configLoading,
                                      configSaving: props.configSaving,
                                      configDirty: props.configDirty,
                                      filter: props.skillsFilter,
                                      onFilterChange: props.onSkillsFilterChange,
                                      onRefresh: props.onSkillsRefresh,
                                      onToggle: props.onAgentSkillToggle,
                                      onClear: props.onAgentSkillsClear,
                                      onDisableAll: props.onAgentSkillsDisableAll,
                                      onConfigReload: props.onConfigReload,
                                      onConfigSave: props.onConfigSave,
                                    })
                                  : nothing
                              }
                              ${
                                props.activePanel === "channels"
                                  ? renderAgentChannels({
                                      agent: selectedAgent,
                                      defaultId,
                                      configForm: props.configForm,
                                      agentFilesList: props.agentFilesList,
                                      agentIdentity:
                                        props.agentIdentityById[selectedAgent.id] ?? null,
                                      snapshot: props.channelsSnapshot,
                                      loading: props.channelsLoading,
                                      error: props.channelsError,
                                      lastSuccess: props.channelsLastSuccess,
                                      onRefresh: props.onChannelsRefresh,
                                    })
                                  : nothing
                              }
                              ${
                                props.activePanel === "cron"
                                  ? renderAgentCron({
                                      agent: selectedAgent,
                                      defaultId,
                                      configForm: props.configForm,
                                      agentFilesList: props.agentFilesList,
                                      agentIdentity:
                                        props.agentIdentityById[selectedAgent.id] ?? null,
                                      jobs: props.cronJobs,
                                      status: props.cronStatus,
                                      loading: props.cronLoading,
                                      error: props.cronError,
                                      onRefresh: props.onCronRefresh,
                                    })
                                  : nothing
                              }
                              ${
                                props.activePanel === "workplan" && props.workPlan
                                  ? renderAgentsWorkPlan(props.workPlan)
                                  : nothing
                              }
                              ${
                                props.activePanel === "sandbox"
                                  ? renderAgentSandbox({
                                      agent: selectedAgent,
                                      defaultId,
                                      configForm: props.configForm,
                                      agentFilesList: props.agentFilesList,
                                      agentIdentity:
                                        props.agentIdentityById[selectedAgent.id] ?? null,
                                      configLoading: props.configLoading,
                                      configSaving: props.configSaving,
                                      configDirty: props.configDirty,
                                      onSandboxChange: props.onSandboxChange,
                                      onConfigReload: props.onConfigReload,
                                      onConfigSave: props.onConfigSave,
                                    })
                                  : nothing
                              }
                            `
                      }
                    </section>
                  </div>
                `
      }
    </div>
  `;
}

function renderAgentHeader(
  agent: AgentsListResult["agents"][number],
  defaultId: string | null,
  agentIdentity: AgentIdentityResult | null,
  configForm: Record<string, unknown> | null,
  configLoading: boolean,
  configSaving: boolean,
  onDeleteAgent: (agentId: string) => void,
) {
  const badge = agentBadgeText(agent.id, defaultId);
  const displayName = normalizeAgentLabel(agent);
  const purpose = agentIdentity?.purpose?.trim() || undefined;
  const subtitle = purpose || agent.identity?.theme?.trim() || "Agent workspace and routing.";
  const emoji = resolveAgentEmoji(agent, agentIdentity);
  const isDefault = Boolean(defaultId && agent.id === defaultId);
  return html`
    <section class="card agent-header">
      <div class="agent-header-main">
        <div class="agent-avatar agent-avatar--lg">${emoji || displayName.slice(0, 1)}</div>
        <div>
          <div class="card-title">${displayName}</div>
          <div class="card-sub">${subtitle}</div>
        </div>
      </div>
      <div class="agent-header-meta">
        <div class="mono">${agent.id}</div>
        ${badge ? html`<span class="agent-pill">${badge}</span>` : nothing}
        ${purpose ? html`<span class="agent-pill">Role</span>` : nothing}
        <button
          type="button"
          class="btn danger btn--sm"
          ?disabled=${!configForm || configLoading || configSaving || isDefault}
          title=${isDefault ? AGENT_HINTS.deleteDefaultBlocked : AGENT_HINTS.delete}
          aria-label=${isDefault ? AGENT_HINTS.deleteDefaultBlocked : AGENT_HINTS.delete}
          @click=${() => {
            if (
              window.confirm(
                `Remove agent "${agent.id}" from agents.list? Workspace files are not deleted.`,
              )
            ) {
              onDeleteAgent(agent.id);
            }
          }}
        >
          Delete
        </button>
      </div>
    </section>
  `;
}

function renderAgentTabs(active: AgentsPanel, onSelect: (panel: AgentsPanel) => void) {
  const tabs: Array<{ id: AgentsPanel; label: string }> = [
    { id: "overview", label: "Overview" },
    { id: "files", label: "Files" },
    { id: "tools", label: "Tools" },
    { id: "skills", label: "Skills" },
    { id: "channels", label: "Channels" },
    { id: "cron", label: "Cron Jobs" },
    { id: "workplan", label: "Work plan" },
    { id: "sandbox", label: "Sandbox" },
  ];
  return html`
    <div class="agent-tabs">
      ${tabs.map(
        (tab) => html`
          <button
            class="agent-tab ${active === tab.id ? "active" : ""}"
            type="button"
            title=${hintForAgentPanel(tab.id)}
            aria-label=${`${tab.label}: ${hintForAgentPanel(tab.id)}`}
            aria-current=${active === tab.id ? "page" : nothing}
            @click=${() => onSelect(tab.id)}
          >
            ${tab.label}
          </button>
        `,
      )}
    </div>
  `;
}

function formatConfigIssue(issue: unknown): string {
  if (typeof issue === "string") {
    return issue;
  }
  if (issue && typeof issue === "object" && "message" in issue) {
    const msg = (issue as { message?: unknown }).message;
    if (typeof msg === "string") {
      return msg;
    }
  }
  try {
    return JSON.stringify(issue);
  } catch {
    return String(issue);
  }
}

function renderAgentOverview(params: {
  agent: AgentsListResult["agents"][number];
  defaultId: string | null;
  configForm: Record<string, unknown> | null;
  configIssues: unknown[];
  agentFilesList: AgentsFilesListResult | null;
  agentIdentity: AgentIdentityResult | null;
  agentIdentityLoading: boolean;
  agentIdentityError: string | null;
  configLoading: boolean;
  configSaving: boolean;
  configDirty: boolean;
  onConfigReload: () => void;
  onConfigSave: () => void;
  onSetInstanceDefault: (agentId: string) => void;
  onRegisterAgent: (agentId: string) => void;
  onAgentFieldChange: (
    agentId: string,
    field: import("../controllers/agents-config.ts").AgentFieldPatch,
  ) => void;
}) {
  const {
    agent,
    configForm,
    configIssues,
    agentFilesList,
    agentIdentity,
    agentIdentityLoading,
    agentIdentityError,
    configLoading,
    configSaving,
    configDirty,
    onConfigReload,
    onConfigSave,
    onSetInstanceDefault,
    onRegisterAgent,
    onAgentFieldChange,
  } = params;
  const config = resolveAgentConfig(configForm, agent.id);
  const workspaceFromFiles =
    agentFilesList && agentFilesList.agentId === agent.id ? agentFilesList.workspace : null;
  const workspace =
    workspaceFromFiles || config.entry?.workspace || config.defaults?.workspace || "default";
  const model = config.entry?.model
    ? resolveModelLabel(config.entry?.model)
    : resolveModelLabel(config.defaults?.model);
  const defaultModel = resolveModelLabel(config.defaults?.model);
  const modelPrimary =
    resolveModelPrimary(config.entry?.model) || (model !== "-" ? normalizeModelValue(model) : null);
  const defaultPrimary =
    resolveModelPrimary(config.defaults?.model) ||
    (defaultModel !== "-" ? normalizeModelValue(defaultModel) : null);
  const effectivePrimary = modelPrimary ?? defaultPrimary ?? null;
  const modelFallbacks = resolveModelFallbacks(config.entry?.model);
  const fallbackText = modelFallbacks ? modelFallbacks.join(", ") : "";
  const identityName =
    agentIdentity?.name?.trim() ||
    agent.identity?.name?.trim() ||
    agent.name?.trim() ||
    config.entry?.name ||
    "-";
  const resolvedEmoji = resolveAgentEmoji(agent, agentIdentity);
  const identityEmoji = resolvedEmoji || "-";
  const skillFilter = Array.isArray(config.entry?.skills) ? config.entry?.skills : null;
  const skillCount = skillFilter?.length ?? null;
  const identityStatus = agentIdentityLoading
    ? "Loading…"
    : agentIdentityError
      ? "Unavailable"
      : "";
  const isDefault = Boolean(params.defaultId && agent.id === params.defaultId);
  const hasConfigListEntry = Boolean(config.entry);
  const issues = Array.isArray(configIssues) ? configIssues : [];
  /**
   * The role this agent is bound to.
   *
   * JAISIU-4515 §4 — the model is assigned to the ROLE, so the agent
   * panel points the operator at the role rather than offering a
   * per-agent choice.
   */
  const agentRoleId = config.entry?.roleId?.trim() || null;
  return html`
    <section class="card">
      <div class="card-title">Overview</div>
      <div class="card-sub">Edit agent config (agents.list). Save to apply on the gateway.</div>
      ${
        issues.length > 0
          ? html`
              <div class="callout warn" style="margin-top: 12px;">
                <div style="font-weight: 600; margin-bottom: 6px;">
                  Configuration issues (${issues.length})
                </div>
                <ul style="margin: 0; padding-left: 18px; font-size: 13px;">
                  ${issues.map((issue) => html`<li>${formatConfigIssue(issue)}</li>`)}
                </ul>
                <div class="muted" style="margin-top: 8px; font-size: 12px;">
                  Fix these in the Config tab or raw JSON, then reload config here.
                </div>
              </div>
            `
          : nothing
      }
      <div class="row" style="margin-top: 16px; align-items: center; gap: 12px; flex-wrap: wrap;">
        <div style="flex: 1; min-width: 200px;">
          <div class="label">Instance default agent</div>
          <div class="muted" style="font-size: 13px; margin-top: 4px;">
            ${
              isDefault
                ? "This agent is used when the gateway needs a default agent (e.g. unscoped chat)."
                : "Mark this agent as default in config (agents.list[].default). Other defaults are cleared."
            }
            ${
              !hasConfigListEntry
                ? html`
                    <strong>This agent is not listed in agents.list</strong> — add it under Config
                    to set it as default.
                  `
                : nothing
            }
          </div>
        </div>
        <button
          class="btn btn--sm primary"
          type="button"
          ?disabled=${!configForm || configLoading || configSaving || !hasConfigListEntry || isDefault}
          title=${
            !hasConfigListEntry
              ? AGENT_HINTS.setInstanceDefaultBlocked
              : AGENT_HINTS.setInstanceDefault
          }
          aria-label=${
            !hasConfigListEntry
              ? AGENT_HINTS.setInstanceDefaultBlocked
              : AGENT_HINTS.setInstanceDefault
          }
          @click=${() => onSetInstanceDefault(agent.id)}
        >
          ${isDefault ? "Instance default" : "Set as instance default"}
        </button>
      </div>
      ${renderAgentOverviewEditor({
        agent,
        configEntry: config.entry,
        hasConfigListEntry,
        workspacePreview: workspace,
        identityName,
        identityStatus,
        configForm,
        configLoading,
        configSaving,
        onRegisterAgent,
        onAgentFieldChange,
      })}
      <div class="agents-overview-grid" style="margin-top: 16px;">
        <div class="agent-kv">
          <div class="label">Primary Model</div>
          <div class="mono">${model}</div>
        </div>
        <div class="agent-kv">
          <div class="label">Default</div>
          <div>${isDefault ? "yes" : "no"}</div>
        </div>
        <div class="agent-kv">
          <div class="label">Skills Filter</div>
          <div>${skillFilter ? `${skillCount} selected` : "all skills"}</div>
        </div>
      </div>

      <div class="agent-model-select" style="margin-top: 20px;">
        <div class="label">Model</div>
        <div class="row" style="gap: 12px; flex-wrap: wrap;">
          <div class="field" style="min-width: 260px; flex: 1;">
            <span>Primary model</span>
            <div class="mono" data-testid="agent-effective-model">
              ${effectivePrimary ?? "Inherit from role"}
            </div>
          </div>
          <div class="field" style="min-width: 260px; flex: 1;">
            <span>Fallbacks</span>
            <div class="mono" data-testid="agent-effective-fallbacks">
              ${fallbackText || "None"}
            </div>
          </div>
        </div>
        <div class="muted" style="font-size: 12px; margin-top: 8px;">
          JAISIU-4515 §4 — a model is assigned to a ROLE, not to an individual agent, so every
          instance of the role uses the same one.
          ${
            agentRoleId
              ? html`Set it on the role <span class="mono">${agentRoleId}</span> in Fleet → Roles.`
              : html`This agent is not bound to a role yet, so it uses the platform default model.`
          }
        </div>
        <div class="row" style="justify-content: flex-end; gap: 8px;">
          <button
            class="btn btn--sm"
            ?disabled=${configLoading}
            title=${CONFIG_HINTS.reload}
            aria-label=${CONFIG_HINTS.reload}
            @click=${onConfigReload}
          >
            Reload Config
          </button>
          <button
            class="btn btn--sm primary"
            ?disabled=${configSaving || !configDirty}
            title=${CONFIG_HINTS.save}
            aria-label=${CONFIG_HINTS.save}
            @click=${onConfigSave}
          >
            ${configSaving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </section>
  `;
}

function renderAgentContextCard(context: AgentContext, subtitle: string) {
  return html`
    <section class="card">
      <div class="card-title">Agent Context</div>
      <div class="card-sub">${subtitle}</div>
      <div class="agents-overview-grid" style="margin-top: 16px;">
        <div class="agent-kv">
          <div class="label">Workspace</div>
          <div class="mono">${context.workspace}</div>
        </div>
        <div class="agent-kv">
          <div class="label">Primary Model</div>
          <div class="mono">${context.model}</div>
        </div>
        <div class="agent-kv">
          <div class="label">Identity Name</div>
          <div>${context.identityName}</div>
        </div>
        <div class="agent-kv">
          <div class="label">Identity Emoji</div>
          <div>${context.identityEmoji}</div>
        </div>
        <div class="agent-kv">
          <div class="label">Skills Filter</div>
          <div>${context.skillsLabel}</div>
        </div>
        <div class="agent-kv">
          <div class="label">Default</div>
          <div>${context.isDefault ? "yes" : "no"}</div>
        </div>
      </div>
    </section>
  `;
}

type ChannelSummaryEntry = {
  id: string;
  label: string;
  accounts: ChannelAccountSnapshot[];
};

function resolveChannelLabel(snapshot: ChannelsStatusSnapshot, id: string) {
  const meta = snapshot.channelMeta?.find((entry) => entry.id === id);
  if (meta?.label) {
    return meta.label;
  }
  return snapshot.channelLabels?.[id] ?? id;
}

function resolveChannelEntries(snapshot: ChannelsStatusSnapshot | null): ChannelSummaryEntry[] {
  if (!snapshot) {
    return [];
  }
  const ids = new Set<string>();
  for (const id of snapshot.channelOrder ?? []) {
    ids.add(id);
  }
  for (const entry of snapshot.channelMeta ?? []) {
    ids.add(entry.id);
  }
  for (const id of Object.keys(snapshot.channelAccounts ?? {})) {
    ids.add(id);
  }
  const ordered: string[] = [];
  const seed = snapshot.channelOrder?.length ? snapshot.channelOrder : Array.from(ids);
  for (const id of seed) {
    if (!ids.has(id)) {
      continue;
    }
    ordered.push(id);
    ids.delete(id);
  }
  for (const id of ids) {
    ordered.push(id);
  }
  return ordered.map((id) => ({
    id,
    label: resolveChannelLabel(snapshot, id),
    accounts: snapshot.channelAccounts?.[id] ?? [],
  }));
}

const CHANNEL_EXTRA_FIELDS = ["groupPolicy", "streamMode", "dmPolicy"] as const;

function resolveChannelConfigValue(
  configForm: Record<string, unknown> | null,
  channelId: string,
): Record<string, unknown> | null {
  if (!configForm) {
    return null;
  }
  const channels = (configForm.channels ?? {}) as Record<string, unknown>;
  const fromChannels = channels[channelId];
  if (fromChannels && typeof fromChannels === "object") {
    return fromChannels as Record<string, unknown>;
  }
  const fallback = configForm[channelId];
  if (fallback && typeof fallback === "object") {
    return fallback as Record<string, unknown>;
  }
  return null;
}

function formatChannelExtraValue(raw: unknown): string {
  if (raw == null) {
    return "n/a";
  }
  if (typeof raw === "string" || typeof raw === "number" || typeof raw === "boolean") {
    return String(raw);
  }
  try {
    return JSON.stringify(raw);
  } catch {
    return "n/a";
  }
}

function resolveChannelExtras(
  configForm: Record<string, unknown> | null,
  channelId: string,
): Array<{ label: string; value: string }> {
  const value = resolveChannelConfigValue(configForm, channelId);
  if (!value) {
    return [];
  }
  return CHANNEL_EXTRA_FIELDS.flatMap((field) => {
    if (!(field in value)) {
      return [];
    }
    return [{ label: field, value: formatChannelExtraValue(value[field]) }];
  });
}

function summarizeChannelAccounts(accounts: ChannelAccountSnapshot[]) {
  let connected = 0;
  let configured = 0;
  let enabled = 0;
  for (const account of accounts) {
    const probeOk =
      account.probe && typeof account.probe === "object" && "ok" in account.probe
        ? Boolean((account.probe as { ok?: unknown }).ok)
        : false;
    const isConnected = account.connected === true || account.running === true || probeOk;
    if (isConnected) {
      connected += 1;
    }
    if (account.configured) {
      configured += 1;
    }
    if (account.enabled) {
      enabled += 1;
    }
  }
  return {
    total: accounts.length,
    connected,
    configured,
    enabled,
  };
}

function renderAgentChannels(params: {
  agent: AgentsListResult["agents"][number];
  defaultId: string | null;
  configForm: Record<string, unknown> | null;
  agentFilesList: AgentsFilesListResult | null;
  agentIdentity: AgentIdentityResult | null;
  snapshot: ChannelsStatusSnapshot | null;
  loading: boolean;
  error: string | null;
  lastSuccess: number | null;
  onRefresh: () => void;
}) {
  const context = buildAgentContext(
    params.agent,
    params.configForm,
    params.agentFilesList,
    params.defaultId,
    params.agentIdentity,
  );
  const entries = resolveChannelEntries(params.snapshot);
  const lastSuccessLabel = params.lastSuccess ? formatAgo(params.lastSuccess) : "never";
  return html`
    <section class="grid grid-cols-2">
      ${renderAgentContextCard(context, "Workspace, identity, and model configuration.")}
      <section class="card">
        <div class="row" style="justify-content: space-between;">
          <div>
            <div class="card-title">Channels</div>
            <div class="card-sub">Gateway-wide channel status snapshot.</div>
          </div>
          <button
            class="btn btn--sm"
            ?disabled=${params.loading}
            title=${AGENT_HINTS.refreshChannels}
            aria-label=${AGENT_HINTS.refreshChannels}
            @click=${params.onRefresh}
          >
            ${params.loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
        <div class="muted" style="margin-top: 8px;">Last refresh: ${lastSuccessLabel}</div>
        ${
          params.error
            ? html`<div class="callout danger" style="margin-top: 12px;">${params.error}</div>`
            : nothing
        }
        ${
          !params.snapshot
            ? html`
                <div class="callout info" style="margin-top: 12px">
                  Load channels to see live status.
                </div>
              `
            : nothing
        }
        ${
          entries.length === 0
            ? html` <div class="muted" style="margin-top: 16px">No channels found.</div> `
            : html`
                <div class="list" style="margin-top: 16px;">
                  ${entries.map((entry) => {
                    const summary = summarizeChannelAccounts(entry.accounts);
                    const status = summary.total
                      ? `${summary.connected}/${summary.total} connected`
                      : "no accounts";
                    const config = summary.configured
                      ? `${summary.configured} configured`
                      : "not configured";
                    const enabled = summary.total ? `${summary.enabled} enabled` : "disabled";
                    const extras = resolveChannelExtras(params.configForm, entry.id);
                    return html`
                      <div class="list-item">
                        <div class="list-main">
                          <div class="list-title">${entry.label}</div>
                          <div class="list-sub mono">${entry.id}</div>
                        </div>
                        <div class="list-meta">
                          <div>${status}</div>
                          <div>${config}</div>
                          <div>${enabled}</div>
                          ${
                            extras.length > 0
                              ? extras.map(
                                  (extra) => html`<div>${extra.label}: ${extra.value}</div>`,
                                )
                              : nothing
                          }
                        </div>
                      </div>
                    `;
                  })}
                </div>
              `
        }
      </section>
    </section>
  `;
}

function renderAgentCron(params: {
  agent: AgentsListResult["agents"][number];
  defaultId: string | null;
  configForm: Record<string, unknown> | null;
  agentFilesList: AgentsFilesListResult | null;
  agentIdentity: AgentIdentityResult | null;
  jobs: CronJob[];
  status: CronStatus | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}) {
  const context = buildAgentContext(
    params.agent,
    params.configForm,
    params.agentFilesList,
    params.defaultId,
    params.agentIdentity,
  );
  const jobs = params.jobs.filter((job) => job.agentId === params.agent.id);
  return html`
    <section class="grid grid-cols-2">
      ${renderAgentContextCard(context, "Workspace and scheduling targets.")}
      <section class="card">
        <div class="row" style="justify-content: space-between;">
          <div>
            <div class="card-title">Scheduler</div>
            <div class="card-sub">Gateway cron status.</div>
          </div>
          <button
            class="btn btn--sm"
            ?disabled=${params.loading}
            title=${AGENT_HINTS.refreshCron}
            aria-label=${AGENT_HINTS.refreshCron}
            @click=${params.onRefresh}
          >
            ${params.loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
        <div class="stat-grid" style="margin-top: 16px;">
          <div class="stat">
            <div class="stat-label">Enabled</div>
            <div class="stat-value">
              ${params.status ? (params.status.enabled ? "Yes" : "No") : "n/a"}
            </div>
          </div>
          <div class="stat">
            <div class="stat-label">Jobs</div>
            <div class="stat-value">${params.status?.jobs ?? "n/a"}</div>
          </div>
          <div class="stat">
            <div class="stat-label">Next wake</div>
            <div class="stat-value">${formatNextRun(params.status?.nextWakeAtMs ?? null)}</div>
          </div>
        </div>
        ${
          params.error
            ? html`<div class="callout danger" style="margin-top: 12px;">${params.error}</div>`
            : nothing
        }
      </section>
    </section>
    <section class="card">
      <div class="card-title">Agent Cron Jobs</div>
      <div class="card-sub">Scheduled jobs targeting this agent.</div>
      ${
        jobs.length === 0
          ? html` <div class="muted" style="margin-top: 16px">No jobs assigned.</div> `
          : html`
              <div class="list" style="margin-top: 16px;">
                ${jobs.map(
                  (job) => html`
                    <div class="list-item">
                      <div class="list-main">
                        <div class="list-title">${job.name}</div>
                        ${job.description ? html`<div class="list-sub">${job.description}</div>` : nothing}
                        <div class="chip-row" style="margin-top: 6px;">
                          <span class="chip">${formatCronSchedule(job)}</span>
                          <span class="chip ${job.enabled ? "chip-ok" : "chip-warn"}">
                            ${job.enabled ? "enabled" : "disabled"}
                          </span>
                          <span class="chip">${job.sessionTarget}</span>
                        </div>
                      </div>
                      <div class="list-meta">
                        <div class="mono">${formatCronState(job)}</div>
                        <div class="muted">${formatCronPayload(job)}</div>
                      </div>
                    </div>
                  `,
                )}
              </div>
            `
      }
    </section>
  `;
}

function renderAgentFiles(params: {
  agentId: string;
  agentFilesList: AgentsFilesListResult | null;
  agentFilesLoading: boolean;
  agentFilesError: string | null;
  agentFileActive: string | null;
  agentFileContents: Record<string, string>;
  agentFileDrafts: Record<string, string>;
  agentFileSaving: boolean;
  onLoadFiles: (agentId: string) => void;
  onSelectFile: (name: string) => void;
  onFileDraftChange: (name: string, content: string) => void;
  onFileReset: (name: string) => void;
  onFileSave: (name: string) => void;
}) {
  const list = params.agentFilesList?.agentId === params.agentId ? params.agentFilesList : null;
  const files = list?.files ?? [];
  const active = params.agentFileActive ?? null;
  const activeEntry = active ? (files.find((file) => file.name === active) ?? null) : null;
  const baseContent = active ? (params.agentFileContents[active] ?? "") : "";
  const draft = active ? (params.agentFileDrafts[active] ?? baseContent) : "";
  const isDirty = active ? draft !== baseContent : false;

  return html`
    <section class="card">
      <div class="row" style="justify-content: space-between;">
        <div>
          <div class="card-title">Core Files</div>
          <div class="card-sub">Bootstrap persona, identity, and tool guidance.</div>
        </div>
        <button
          class="btn btn--sm"
          ?disabled=${params.agentFilesLoading}
          title=${AGENT_HINTS.refreshFiles}
          aria-label=${AGENT_HINTS.refreshFiles}
          @click=${() => params.onLoadFiles(params.agentId)}
        >
          ${params.agentFilesLoading ? "Loading…" : "Refresh"}
        </button>
      </div>
      ${list ? html`<div class="muted mono" style="margin-top: 8px;">Workspace: ${list.workspace}</div>` : nothing}
      ${
        params.agentFilesError
          ? html`<div class="callout danger" style="margin-top: 12px;">
              ${params.agentFilesError}
            </div>`
          : nothing
      }
      ${
        !list
          ? html`
              <div class="callout info" style="margin-top: 12px">
                Load the agent workspace files to edit core instructions.
              </div>
            `
          : html`
              <div class="agent-files-grid" style="margin-top: 16px;">
                <div class="agent-files-list">
                  ${
                    files.length === 0
                      ? html` <div class="muted">No files found.</div> `
                      : files.map((file) =>
                          renderAgentFileRow(file, active, () => params.onSelectFile(file.name)),
                        )
                  }
                </div>
                <div class="agent-files-editor">
                  ${
                    !activeEntry
                      ? html` <div class="muted">Select a file to edit.</div> `
                      : html`
                          <div class="agent-file-header">
                            <div>
                              <div class="agent-file-title mono">${activeEntry.name}</div>
                              <div class="agent-file-sub mono">${activeEntry.path}</div>
                            </div>
                            <div class="agent-file-actions">
                              <button
                                class="btn btn--sm"
                                ?disabled=${!isDirty}
                                title=${AGENT_HINTS.fileReset}
                                aria-label=${AGENT_HINTS.fileReset}
                                @click=${() => params.onFileReset(activeEntry.name)}
                              >
                                Reset
                              </button>
                              <button
                                class="btn btn--sm primary"
                                ?disabled=${params.agentFileSaving || !isDirty}
                                title=${AGENT_HINTS.fileSave}
                                aria-label=${AGENT_HINTS.fileSave}
                                @click=${() => params.onFileSave(activeEntry.name)}
                              >
                                ${params.agentFileSaving ? "Saving…" : "Save"}
                              </button>
                            </div>
                          </div>
                          ${
                            activeEntry.missing
                              ? html`
                                  <div class="callout info" style="margin-top: 10px">
                                    This file is missing. Saving will create it in the agent
                                    workspace.
                                  </div>
                                `
                              : nothing
                          }
                          <label class="field" style="margin-top: 12px;">
                            <span>Content</span>
                            <textarea
                              .value=${draft}
                              @input=${(e: Event) =>
                                params.onFileDraftChange(
                                  activeEntry.name,
                                  (e.target as HTMLTextAreaElement).value,
                                )}
                            ></textarea>
                          </label>
                        `
                  }
                </div>
              </div>
            `
      }
    </section>
  `;
}

function renderAgentFileRow(file: AgentFileEntry, active: string | null, onSelect: () => void) {
  const status = file.missing
    ? "Missing"
    : `${formatBytes(file.size)} · ${formatAgo(file.updatedAtMs ?? null)}`;
  const fileHint = hintAgentFile(file.name, file.missing);
  return html`
    <button
      type="button"
      class="agent-file-row ${active === file.name ? "active" : ""}"
      title=${fileHint}
      aria-label=${fileHint}
      @click=${onSelect}
    >
      <div>
        <div class="agent-file-name mono">${file.name}</div>
        <div class="agent-file-meta">${status}</div>
      </div>
      ${file.missing ? html` <span class="agent-pill warn">missing</span> ` : nothing}
    </button>
  `;
}

function renderAgentTools(params: {
  agentId: string;
  configForm: Record<string, unknown> | null;
  configLoading: boolean;
  configSaving: boolean;
  configDirty: boolean;
  onProfileChange: (agentId: string, profile: string | null, clearAllow: boolean) => void;
  onOverridesChange: (agentId: string, alsoAllow: string[], deny: string[]) => void;
  onConfigReload: () => void;
  onConfigSave: () => void;
}) {
  const config = resolveAgentConfig(params.configForm, params.agentId);
  const agentTools = config.entry?.tools ?? {};
  const globalTools = config.globalTools ?? {};
  const profile = agentTools.profile ?? globalTools.profile ?? "full";
  const profileSource = agentTools.profile
    ? "agent override"
    : globalTools.profile
      ? "global default"
      : "default";
  const hasAgentAllow = Array.isArray(agentTools.allow) && agentTools.allow.length > 0;
  const hasGlobalAllow = Array.isArray(globalTools.allow) && globalTools.allow.length > 0;
  const editable =
    Boolean(params.configForm) && !params.configLoading && !params.configSaving && !hasAgentAllow;
  const alsoAllow = hasAgentAllow
    ? []
    : Array.isArray(agentTools.alsoAllow)
      ? agentTools.alsoAllow
      : [];
  const deny = hasAgentAllow ? [] : Array.isArray(agentTools.deny) ? agentTools.deny : [];
  const basePolicy = hasAgentAllow
    ? { allow: agentTools.allow ?? [], deny: agentTools.deny ?? [] }
    : (resolveToolProfilePolicy(profile) ?? undefined);
  const toolIds = TOOL_SECTIONS.flatMap((section) => section.tools.map((tool) => tool.id));

  const resolveAllowed = (toolId: string) => {
    const baseAllowed = isAllowedByPolicy(toolId, basePolicy);
    const extraAllowed = matchesList(toolId, alsoAllow);
    const denied = matchesList(toolId, deny);
    const allowed = (baseAllowed || extraAllowed) && !denied;
    return {
      allowed,
      baseAllowed,
      denied,
    };
  };
  const enabledCount = toolIds.filter((toolId) => resolveAllowed(toolId).allowed).length;

  const updateTool = (toolId: string, nextEnabled: boolean) => {
    const nextAllow = new Set(
      alsoAllow.map((entry) => normalizeToolName(entry)).filter((entry) => entry.length > 0),
    );
    const nextDeny = new Set(
      deny.map((entry) => normalizeToolName(entry)).filter((entry) => entry.length > 0),
    );
    const baseAllowed = resolveAllowed(toolId).baseAllowed;
    const normalized = normalizeToolName(toolId);
    if (nextEnabled) {
      nextDeny.delete(normalized);
      if (!baseAllowed) {
        nextAllow.add(normalized);
      }
    } else {
      nextAllow.delete(normalized);
      nextDeny.add(normalized);
    }
    params.onOverridesChange(params.agentId, [...nextAllow], [...nextDeny]);
  };

  const updateAll = (nextEnabled: boolean) => {
    const nextAllow = new Set(
      alsoAllow.map((entry) => normalizeToolName(entry)).filter((entry) => entry.length > 0),
    );
    const nextDeny = new Set(
      deny.map((entry) => normalizeToolName(entry)).filter((entry) => entry.length > 0),
    );
    for (const toolId of toolIds) {
      const baseAllowed = resolveAllowed(toolId).baseAllowed;
      const normalized = normalizeToolName(toolId);
      if (nextEnabled) {
        nextDeny.delete(normalized);
        if (!baseAllowed) {
          nextAllow.add(normalized);
        }
      } else {
        nextAllow.delete(normalized);
        nextDeny.add(normalized);
      }
    }
    params.onOverridesChange(params.agentId, [...nextAllow], [...nextDeny]);
  };

  return html`
    <section class="card">
      <div class="row" style="justify-content: space-between;">
        <div>
          <div class="card-title">Tool Access</div>
          <div class="card-sub">
            Profile + per-tool overrides for this agent.
            <span class="mono">${enabledCount}/${toolIds.length}</span> enabled.
          </div>
        </div>
        <div class="row" style="gap: 8px;">
          <button
            class="btn btn--sm"
            ?disabled=${!editable}
            title=${AGENT_HINTS.toolsEnableAll}
            aria-label=${AGENT_HINTS.toolsEnableAll}
            @click=${() => updateAll(true)}
          >
            Enable All
          </button>
          <button
            class="btn btn--sm"
            ?disabled=${!editable}
            title=${AGENT_HINTS.toolsDisableAll}
            aria-label=${AGENT_HINTS.toolsDisableAll}
            @click=${() => updateAll(false)}
          >
            Disable All
          </button>
          <button
            class="btn btn--sm"
            ?disabled=${params.configLoading}
            title=${CONFIG_HINTS.reload}
            aria-label=${CONFIG_HINTS.reload}
            @click=${params.onConfigReload}
          >
            Reload Config
          </button>
          <button
            class="btn btn--sm primary"
            ?disabled=${params.configSaving || !params.configDirty}
            title=${CONFIG_HINTS.save}
            aria-label=${CONFIG_HINTS.save}
            @click=${params.onConfigSave}
          >
            ${params.configSaving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      ${
        !params.configForm
          ? html`
              <div class="callout info" style="margin-top: 12px">
                Load the gateway config to adjust tool profiles.
              </div>
            `
          : nothing
      }
      ${
        hasAgentAllow
          ? html`
              <div class="callout info" style="margin-top: 12px">
                This agent is using an explicit allowlist in config. Tool overrides are managed in
                the Config tab.
              </div>
            `
          : nothing
      }
      ${
        hasGlobalAllow
          ? html`
              <div class="callout info" style="margin-top: 12px">
                Global tools.allow is set. Agent overrides cannot enable tools that are globally
                blocked.
              </div>
            `
          : nothing
      }

      <div class="agent-tools-meta" style="margin-top: 16px;">
        <div class="agent-kv">
          <div class="label">Profile</div>
          <div class="mono">${profile}</div>
        </div>
        <div class="agent-kv">
          <div class="label">Source</div>
          <div>${profileSource}</div>
        </div>
        ${
          params.configDirty
            ? html`
                <div class="agent-kv">
                  <div class="label">Status</div>
                  <div class="mono">unsaved</div>
                </div>
              `
            : nothing
        }
      </div>

      <div class="agent-tools-presets" style="margin-top: 16px;">
        <div class="label">Quick Presets</div>
        <div class="agent-tools-buttons">
          ${PROFILE_OPTIONS.map(
            (option) => html`
              <button
                class="btn btn--sm ${profile === option.id ? "active" : ""}"
                ?disabled=${!editable}
                title=${AGENT_HINTS.toolsProfile(option.label)}
                aria-label=${AGENT_HINTS.toolsProfile(option.label)}
                @click=${() => params.onProfileChange(params.agentId, option.id, true)}
              >
                ${option.label}
              </button>
            `,
          )}
          <button
            class="btn btn--sm"
            ?disabled=${!editable}
            title=${AGENT_HINTS.toolsInherit}
            aria-label=${AGENT_HINTS.toolsInherit}
            @click=${() => params.onProfileChange(params.agentId, null, false)}
          >
            Inherit
          </button>
        </div>
      </div>

      <div class="agent-tools-grid" style="margin-top: 20px;">
        ${TOOL_SECTIONS.map(
          (section) => html`
            <div class="agent-tools-section">
              <div class="agent-tools-header">${section.label}</div>
              <div class="agent-tools-list">
                ${section.tools.map((tool) => {
                  const { allowed } = resolveAllowed(tool.id);
                  return html`
                    <div class="agent-tool-row">
                      <div>
                        <div class="agent-tool-title mono">${tool.label}</div>
                        <div class="agent-tool-sub">${tool.description}</div>
                      </div>
                      <label class="cfg-toggle">
                        <input
                          type="checkbox"
                          .checked=${allowed}
                          ?disabled=${!editable}
                          @change=${(e: Event) =>
                            updateTool(tool.id, (e.target as HTMLInputElement).checked)}
                        />
                        <span class="cfg-toggle__track"></span>
                      </label>
                    </div>
                  `;
                })}
              </div>
            </div>
          `,
        )}
      </div>
    </section>
  `;
}

type SkillGroup = {
  id: string;
  label: string;
  skills: SkillStatusEntry[];
};

const SKILL_SOURCE_GROUPS: Array<{ id: string; label: string; sources: string[] }> = [
  { id: "workspace", label: "Workspace Skills", sources: ["openclaw-workspace"] },
  { id: "built-in", label: "Built-in Skills", sources: ["openclaw-bundled"] },
  { id: "installed", label: "Installed Skills", sources: ["openclaw-managed"] },
  { id: "extra", label: "Extra Skills", sources: ["openclaw-extra"] },
];

function groupSkills(skills: SkillStatusEntry[]): SkillGroup[] {
  const groups = new Map<string, SkillGroup>();
  for (const def of SKILL_SOURCE_GROUPS) {
    groups.set(def.id, { id: def.id, label: def.label, skills: [] });
  }
  const builtInGroup = SKILL_SOURCE_GROUPS.find((group) => group.id === "built-in");
  const other: SkillGroup = { id: "other", label: "Other Skills", skills: [] };
  for (const skill of skills) {
    const match = skill.bundled
      ? builtInGroup
      : SKILL_SOURCE_GROUPS.find((group) => group.sources.includes(skill.source));
    if (match) {
      groups.get(match.id)?.skills.push(skill);
    } else {
      other.skills.push(skill);
    }
  }
  const ordered = SKILL_SOURCE_GROUPS.map((group) => groups.get(group.id)).filter(
    (group): group is SkillGroup => Boolean(group && group.skills.length > 0),
  );
  if (other.skills.length > 0) {
    ordered.push(other);
  }
  return ordered;
}

function renderAgentSkills(params: {
  agentId: string;
  report: SkillStatusReport | null;
  loading: boolean;
  error: string | null;
  activeAgentId: string | null;
  configForm: Record<string, unknown> | null;
  configLoading: boolean;
  configSaving: boolean;
  configDirty: boolean;
  filter: string;
  onFilterChange: (next: string) => void;
  onRefresh: () => void;
  onToggle: (agentId: string, skillName: string, enabled: boolean) => void;
  onClear: (agentId: string) => void;
  onDisableAll: (agentId: string) => void;
  onConfigReload: () => void;
  onConfigSave: () => void;
}) {
  const editable = Boolean(params.configForm) && !params.configLoading && !params.configSaving;
  const config = resolveAgentConfig(params.configForm, params.agentId);
  const allowlist = Array.isArray(config.entry?.skills) ? config.entry?.skills : undefined;
  const allowSet = new Set((allowlist ?? []).map((name) => name.trim()).filter(Boolean));
  const usingAllowlist = allowlist !== undefined;
  const reportReady = Boolean(params.report && params.activeAgentId === params.agentId);
  const rawSkills = reportReady ? (params.report?.skills ?? []) : [];
  const filter = params.filter.trim().toLowerCase();
  const filtered = filter
    ? rawSkills.filter((skill) =>
        [skill.name, skill.description, skill.source].join(" ").toLowerCase().includes(filter),
      )
    : rawSkills;
  const groups = groupSkills(filtered);
  const enabledCount = usingAllowlist
    ? rawSkills.filter((skill) => allowSet.has(skill.name)).length
    : rawSkills.length;
  const totalCount = rawSkills.length;

  return html`
    <section class="card">
      <div class="row" style="justify-content: space-between;">
        <div>
          <div class="card-title">Skills</div>
          <div class="card-sub">
            Per-agent skill allowlist and workspace skills.
            ${totalCount > 0 ? html`<span class="mono">${enabledCount}/${totalCount}</span>` : nothing}
          </div>
        </div>
        <div class="row" style="gap: 8px;">
          <button
            class="btn btn--sm"
            ?disabled=${!editable}
            title=${AGENT_HINTS.skillsUseAll}
            aria-label=${AGENT_HINTS.skillsUseAll}
            @click=${() => params.onClear(params.agentId)}
          >
            Use All
          </button>
          <button
            class="btn btn--sm"
            ?disabled=${!editable}
            title=${AGENT_HINTS.skillsDisableAll}
            aria-label=${AGENT_HINTS.skillsDisableAll}
            @click=${() => params.onDisableAll(params.agentId)}
          >
            Disable All
          </button>
          <button
            class="btn btn--sm"
            ?disabled=${params.configLoading}
            title=${CONFIG_HINTS.reload}
            aria-label=${CONFIG_HINTS.reload}
            @click=${params.onConfigReload}
          >
            Reload Config
          </button>
          <button
            class="btn btn--sm"
            ?disabled=${params.loading}
            title=${AGENT_HINTS.skillsRefresh}
            aria-label=${AGENT_HINTS.skillsRefresh}
            @click=${params.onRefresh}
          >
            ${params.loading ? "Loading…" : "Refresh"}
          </button>
          <button
            class="btn btn--sm primary"
            ?disabled=${params.configSaving || !params.configDirty}
            title=${CONFIG_HINTS.save}
            aria-label=${CONFIG_HINTS.save}
            @click=${params.onConfigSave}
          >
            ${params.configSaving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      ${
        !params.configForm
          ? html`
              <div class="callout info" style="margin-top: 12px">
                Load the gateway config to set per-agent skills.
              </div>
            `
          : nothing
      }
      ${
        usingAllowlist
          ? html`
              <div class="callout info" style="margin-top: 12px">
                This agent uses a custom skill allowlist.
              </div>
            `
          : html`
              <div class="callout info" style="margin-top: 12px">
                All skills are enabled. Disabling any skill will create a per-agent allowlist.
              </div>
            `
      }
      ${
        !reportReady && !params.loading
          ? html`
              <div class="callout info" style="margin-top: 12px">
                Load skills for this agent to view workspace-specific entries.
              </div>
            `
          : nothing
      }
      ${
        params.error
          ? html`<div class="callout danger" style="margin-top: 12px;">${params.error}</div>`
          : nothing
      }

      <div class="filters" style="margin-top: 14px;">
        <label class="field" style="flex: 1;">
          <span>Filter</span>
          <input
            .value=${params.filter}
            @input=${(e: Event) => params.onFilterChange((e.target as HTMLInputElement).value)}
            placeholder="Search skills"
          />
        </label>
        <div class="muted">${filtered.length} shown</div>
      </div>

      ${
        filtered.length === 0
          ? html` <div class="muted" style="margin-top: 16px">No skills found.</div> `
          : html`
              <div class="agent-skills-groups" style="margin-top: 16px;">
                ${groups.map((group) =>
                  renderAgentSkillGroup(group, {
                    agentId: params.agentId,
                    allowSet,
                    usingAllowlist,
                    editable,
                    onToggle: params.onToggle,
                  }),
                )}
              </div>
            `
      }
    </section>
  `;
}

function renderAgentSkillGroup(
  group: SkillGroup,
  params: {
    agentId: string;
    allowSet: Set<string>;
    usingAllowlist: boolean;
    editable: boolean;
    onToggle: (agentId: string, skillName: string, enabled: boolean) => void;
  },
) {
  const collapsedByDefault = group.id === "workspace" || group.id === "built-in";
  return html`
    <details class="agent-skills-group" ?open=${!collapsedByDefault}>
      <summary class="agent-skills-header">
        <span>${group.label}</span>
        <span class="muted">${group.skills.length}</span>
      </summary>
      <div class="list skills-grid">
        ${group.skills.map((skill) =>
          renderAgentSkillRow(skill, {
            agentId: params.agentId,
            allowSet: params.allowSet,
            usingAllowlist: params.usingAllowlist,
            editable: params.editable,
            onToggle: params.onToggle,
          }),
        )}
      </div>
    </details>
  `;
}

function renderAgentSkillRow(
  skill: SkillStatusEntry,
  params: {
    agentId: string;
    allowSet: Set<string>;
    usingAllowlist: boolean;
    editable: boolean;
    onToggle: (agentId: string, skillName: string, enabled: boolean) => void;
  },
) {
  const enabled = params.usingAllowlist ? params.allowSet.has(skill.name) : true;
  const missing = [
    ...skill.missing.bins.map((b) => `bin:${b}`),
    ...skill.missing.env.map((e) => `env:${e}`),
    ...skill.missing.config.map((c) => `config:${c}`),
    ...skill.missing.os.map((o) => `os:${o}`),
  ];
  const reasons: string[] = [];
  if (skill.disabled) {
    reasons.push("disabled");
  }
  if (skill.blockedByAllowlist) {
    reasons.push("blocked by allowlist");
  }
  return html`
    <div class="list-item agent-skill-row">
      <div class="list-main">
        <div class="list-title">${skill.emoji ? `${skill.emoji} ` : ""}${skill.name}</div>
        <div class="list-sub">${skill.description}</div>
        <div class="chip-row" style="margin-top: 6px;">
          <span class="chip">${skill.source}</span>
          <span class="chip ${skill.eligible ? "chip-ok" : "chip-warn"}">
            ${skill.eligible ? "eligible" : "blocked"}
          </span>
          ${skill.disabled ? html` <span class="chip chip-warn">disabled</span> ` : nothing}
        </div>
        ${
          missing.length > 0
            ? html`<div class="muted" style="margin-top: 6px;">Missing: ${missing.join(", ")}</div>`
            : nothing
        }
        ${
          reasons.length > 0
            ? html`<div class="muted" style="margin-top: 6px;">Reason: ${reasons.join(", ")}</div>`
            : nothing
        }
      </div>
      <div class="list-meta">
        <label class="cfg-toggle">
          <input
            type="checkbox"
            .checked=${enabled}
            ?disabled=${!params.editable}
            @change=${(e: Event) =>
              params.onToggle(params.agentId, skill.name, (e.target as HTMLInputElement).checked)}
          />
          <span class="cfg-toggle__track"></span>
        </label>
      </div>
    </div>
  `;
}

type SandboxValue = {
  mode?: "off" | "non-main" | "all";
  workspaceAccess?: "none" | "ro" | "rw";
  sessionToolsVisibility?: "spawned" | "all";
  scope?: "session" | "agent" | "shared";
  perSession?: boolean;
  workspaceRoot?: string;
};

function resolveAgentSandbox(
  configForm: Record<string, unknown> | null,
  agentId: string,
): SandboxValue {
  const cfg = configForm as { agents?: { list?: unknown[] } } | null;
  const list = cfg?.agents?.list ?? [];
  const normalized = normalizeAgentId(agentId);
  const entry = list.find((agent) => {
    const a = agent as { id?: unknown };
    return a?.id != null && normalizeAgentId(String(a.id)) === normalized;
  });
  return (entry as SandboxValue) ?? {};
}

function resolveDefaultSandbox(configForm: Record<string, unknown> | null): SandboxValue {
  // JAISIU-types-sweep: the local cast used to claim `agents.defaults`
  // WAS the sandbox record; it actually nests one level deeper
  // (`agents.defaults.sandbox` — the same shape `agents-config.ts`
  // writes). Corrected the cast instead of reaching through an index
  // signature. `?? {}` already coalesced the missing-defaults case, so
  // behaviour is unchanged.
  const cfg = configForm as { agents?: { defaults?: { sandbox?: SandboxValue } } } | null;
  return cfg?.agents?.defaults?.sandbox ?? {};
}

const SANDBOX_MODE_OPTIONS = [
  { value: "off", label: "Off (host execution)" },
  { value: "non-main", label: "Non-main sessions only" },
  { value: "all", label: "All sessions" },
] as const;

const SANDBOX_WORKSPACE_ACCESS_OPTIONS = [
  { value: "none", label: "None (sandbox workspace)" },
  { value: "ro", label: "Read-only" },
  { value: "rw", label: "Read-write" },
] as const;

const SANDBOX_SESSION_TOOLS_OPTIONS = [
  { value: "spawned", label: "Spawned sessions only" },
  { value: "all", label: "All sessions" },
] as const;

const SANDBOX_SCOPE_OPTIONS = [
  { value: "session", label: "Per session" },
  { value: "agent", label: "Per agent" },
  { value: "shared", label: "Shared across sessions" },
] as const;

function renderSandboxSelect(
  label: string,
  value: string,
  options: readonly { value: string; label: string }[],
  subtitle: string,
  disabled: boolean,
  onChange: (value: string) => void,
) {
  return html`
    <label class="field">
      <span>${label}</span>
      <span class="muted" style="font-size:12px;">${subtitle}</span>
      <select
        .value=${value}
        ?disabled=${disabled}
        @change=${(e: Event) => onChange((e.target as HTMLSelectElement).value)}
      >
        <option value="">inherit default</option>
        ${options.map((opt) => html`<option value=${opt.value}>${opt.label}</option>`)}
      </select>
    </label>
  `;
}

function renderAgentSandbox(params: {
  agent: AgentsListResult["agents"][number];
  defaultId: string | null;
  configForm: Record<string, unknown> | null;
  agentFilesList: AgentsFilesListResult | null;
  agentIdentity: AgentIdentityResult | null;
  configLoading: boolean;
  configSaving: boolean;
  configDirty: boolean;
  onSandboxChange: (
    agentId: string,
    patch: import("../controllers/agents-config.ts").SandboxFieldPatch,
  ) => void;
  onConfigReload: () => void;
  onConfigSave: () => void;
}) {
  const editable = Boolean(params.configForm) && !params.configLoading && !params.configSaving;
  const context = buildAgentContext(
    params.agent,
    params.configForm,
    params.agentFilesList,
    params.defaultId,
    params.agentIdentity,
  );
  const agentSandbox = resolveAgentSandbox(params.configForm, params.agent.id);
  const defaults = resolveDefaultSandbox(params.configForm);

  const effectiveMode = agentSandbox.mode ?? defaults.mode ?? "off";
  const effectiveWorkspaceAccess = agentSandbox.workspaceAccess ?? defaults.workspaceAccess ?? null;
  const effectiveSessionTools =
    agentSandbox.sessionToolsVisibility ?? defaults.sessionToolsVisibility ?? "spawned";
  const effectiveScope = agentSandbox.scope ?? defaults.scope ?? "session";
  const effectiveWorkspaceRoot = agentSandbox.workspaceRoot ?? defaults.workspaceRoot ?? "";

  const handleModeChange = (value: string) => {
    params.onSandboxChange(params.agent.id, {
      mode: value === "" ? null : (value as "off" | "non-main" | "all"),
    });
  };
  const handleWorkspaceAccessChange = (value: string) => {
    params.onSandboxChange(params.agent.id, {
      workspaceAccess: value === "" ? null : (value as "none" | "ro" | "rw"),
    });
  };
  const handleSessionToolsChange = (value: string) => {
    params.onSandboxChange(params.agent.id, {
      sessionToolsVisibility: value === "" ? null : (value as "spawned" | "all"),
    });
  };
  const handleScopeChange = (value: string) => {
    params.onSandboxChange(params.agent.id, {
      scope: value === "" ? null : (value as "session" | "agent" | "shared"),
    });
  };

  return html`
    <section class="grid grid-cols-2">
      ${renderAgentContextCard(context, "Workspace and runtime context.")}
      <section class="card">
        <div class="row" style="justify-content: space-between; align-items: flex-start;">
          <div>
            <div class="card-title">Sandbox Configuration</div>
            <div class="card-sub">Container isolation and workspace access per agent.</div>
          </div>
          <div class="row" style="gap: 8px;">
            <button
              class="btn btn--sm"
              ?disabled=${params.configLoading}
              title=${CONFIG_HINTS.reload}
              aria-label=${CONFIG_HINTS.reload}
              @click=${params.onConfigReload}
            >
              Reload Config
            </button>
            <button
              class="btn btn--sm primary"
              ?disabled=${params.configSaving || !params.configDirty}
              title=${CONFIG_HINTS.save}
              aria-label=${CONFIG_HINTS.save}
              @click=${params.onConfigSave}
            >
              ${params.configSaving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
        ${
          !params.configForm
            ? html`
                <div class="callout info" style="margin-top: 12px">
                  Load the gateway config to edit sandbox settings.
                </div>
              `
            : nothing
        }
        <div class="agents-sandbox-fields" style="margin-top: 16px; display: grid; gap: 16px;">
          ${renderSandboxSelect(
            "Sandbox mode",
            effectiveMode,
            SANDBOX_MODE_OPTIONS,
            "When to run sessions inside sandbox containers.",
            !editable,
            handleModeChange,
          )}
          ${renderSandboxSelect(
            "Workspace access",
            effectiveWorkspaceAccess ?? "",
            SANDBOX_WORKSPACE_ACCESS_OPTIONS,
            "How the agent can access its workspace inside the sandbox.",
            !editable,
            handleWorkspaceAccessChange,
          )}
          ${renderSandboxSelect(
            "Session tools visibility",
            effectiveSessionTools,
            SANDBOX_SESSION_TOOLS_OPTIONS,
            "Which sessions the agent can target via session tools.",
            !editable,
            handleSessionToolsChange,
          )}
          ${renderSandboxSelect(
            "Container scope",
            effectiveScope,
            SANDBOX_SCOPE_OPTIONS,
            "Granularity of sandbox container isolation.",
            !editable,
            handleScopeChange,
          )}
          <label class="field">
            <span>Workspace root</span>
            <span class="muted" style="font-size:12px;">
              Override default root directory for sandbox workspaces.
            </span>
            <input
              .value=${effectiveWorkspaceRoot}
              ?disabled=${!editable}
              placeholder="inherited from defaults or system"
              @input=${(e: Event) =>
                params.onSandboxChange(params.agent.id, {
                  workspaceRoot: (e.target as HTMLInputElement).value,
                })}
            />
          </label>
        </div>
        <div class="agents-sandbox-footer" style="margin-top: 16px;">
          <div class="muted" style="font-size: 13px;">
            Sandbox overrides per-agent. Docker, browser, and prune settings are configured in the
            Config tab (raw YAML / JSON).
          </div>
        </div>
      </section>
    </section>
    <section class="card">
      <div class="card-title">AgentBinding Routing</div>
      <div class="card-sub">
        Route incoming messages to this agent by channel, account, or peer.
      </div>
      <div class="callout info" style="margin-top: 12px;">
        AgentBinding routing is configured in the Config tab (agents.bindings array) or per-channel
        in the Channels section.
      </div>
    </section>
  `;
}
